import React, { useMemo } from 'react';
import {
  faFileAlt,
  faUsers,
  faPeopleGroup,
  faScrewdriverWrench,
  faChartSimple,
  faFilePdf,
  faPhone,
  faCode,
  faLayerGroup,
  faKey,
  faMessage,
  faShoppingBag,
  faFolderOpen,
  faMicrochip,
  faFileSignature,
  faClipboardList,
  faPrint,
  faMobileScreen,
} from '@fortawesome/free-solid-svg-icons';
import { filterNavTreeByPermissions } from 'access/routeAccess';
import { usePermissions } from 'access/usePermission';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  buildFullNavTreeData,
  type NavTreeDataNode,
} from 'navigation/rosieNavTreeData';
import useUserStore from 'store/useUserStore';
import type { NavTreeNode } from '@urbantxt/navigation';

export type { NavTreeNode };

const currentYear = new Date().getFullYear().toString();

function attachIcons(node: NavTreeDataNode): NavTreeNode {
  const iconFor = (): JSX.Element => {
    const t = node.title.toLowerCase();
    if (t.includes('stats') || t.includes('information')) {
      return <FontAwesomeIcon icon={faChartSimple} />;
    }
    if (
      t.includes('user account') ||
      t.includes('accounts') ||
      t.includes('roster') ||
      t.includes('team')
    ) {
      return <FontAwesomeIcon icon={faUsers} />;
    }
    if (
      t.includes('scla') ||
      t.includes('hnc') ||
      t.includes('seals') ||
      t.includes('programs')
    ) {
      return <FontAwesomeIcon icon={faPeopleGroup} />;
    }
    if (t.includes('bagel')) {
      return <span aria-hidden>🥯</span>;
    }
    if (t.includes('sms')) {
      return <FontAwesomeIcon icon={faMessage} />;
    }
    if (t.includes('call')) {
      return <FontAwesomeIcon icon={faPhone} />;
    }
    if (t.includes('script') || t.includes('code')) {
      return <FontAwesomeIcon icon={faCode} />;
    }
    if (t.includes('campaign') || t.includes('batch')) {
      return <FontAwesomeIcon icon={faLayerGroup} />;
    }
    if (t.includes('bitwarden') || t.includes('secrets')) {
      return <FontAwesomeIcon icon={faKey} />;
    }
    if (t.includes('dropbox')) {
      return <FontAwesomeIcon icon={faFolderOpen} />;
    }
    if (t.includes('mobile') || t.includes('mothership')) {
      return <FontAwesomeIcon icon={faMobileScreen} />;
    }
    if (t.includes('merch') || t.includes('shopping')) {
      return <FontAwesomeIcon icon={faShoppingBag} />;
    }
    if (t.includes('equipment')) {
      return <FontAwesomeIcon icon={faMicrochip} />;
    }
    if (t.includes('survey')) {
      return <FontAwesomeIcon icon={faClipboardList} />;
    }
    if (
      t.includes('form') ||
      t.includes('template') ||
      t.includes('signed')
    ) {
      return <FontAwesomeIcon icon={faFileSignature} />;
    }
    if (t.includes('label') || t.includes('print')) {
      return <FontAwesomeIcon icon={faPrint} />;
    }
    if (t.includes('tools')) {
      return <FontAwesomeIcon icon={faScrewdriverWrench} />;
    }
    if (t.includes('upload') || t.includes('pdf')) {
      return <FontAwesomeIcon icon={faFilePdf} />;
    }
    return <FontAwesomeIcon icon={faFileAlt} />;
  };

  const icon = iconFor();
  const children = node.children?.map(attachIcons);

  return {
    id: node.id,
    title: node.title,
    subtitle: node.subtitle,
    sectionTitle: node.sectionTitle,
    icon,
    to: node.to,
    children,
  };
}

export function buildFullNavTree(): NavTreeNode[] {
  return buildFullNavTreeData(currentYear).map(attachIcons);
}

export default function useNavigationLinks() {
  const user = useUserStore(s => s.user);
  const { can } = usePermissions();

  const links = useMemo(() => {
    if (!user) return [];
    return filterNavTreeByPermissions(buildFullNavTree(), can);
  }, [user, can]);

  return { links };
}
